<template>
    <div id="app"
        v-if="ready"

        :class="{
        'has-navigation': hasNavigation,
        'has-footer-bar': hasFooterBar,
        'page-login': login,
        'page-empty': empty,
        'page-backend': backend && ! empty,
        'page-wizard': wizard,
        'touch-device': touchDevice,
    }">

        <div class="update-notification" v-if="updated">
            <h1>{{ $t('page.update.headline') }}</h1><br>
            {{ $t('page.update.text') }}<br><br>
            <a href="#" v-on:click="updateApp()" class="btn">{{ $t('page.update.button') }}</a>
        </div>

        <div class="update-notification" v-if="maintenance">
            <h1>ONDURO will be right back!</h1><br>
            <p>We're performing a maintenance update and will be back in a moment.</p>
            <div><img src="/static/maintenance.gif" style="max-width:90%"></div>
            <p>Please try again later or by clicking on the button below.</p>
            <a href="#" v-on:click="updateApp()" class="btn">Reload</a>
        </div>

        <!-- Templates -->
        <empty v-if="empty" />
        <backend v-if="backend && ! maintenance" />
        <frontend v-if="frontend" />
        <wizard v-if="wizard" />
        <invoice v-if="invoice" />

        <!-- Global components -->
        <modal ref="modal" />
        <flash ref="flash" />
        <widget ref="modal" />
        <loader ref="loader" />
        <toolbox ref="toolbox" />
        <confirm ref="confirm" />
        <notification ref="notification" />
        <university ref="university" v-if="authenticated" />
        <lesson ref="lesson" />
        <tooltip />

    </div>
</template>

<script>
import Vue from 'vue';
import Modal from './global/Modal';
import Flash from './global/Flash';
import Widget from './global/Widget';
import Confirm from './global/Confirm';
import Loader from './global/Loader';
import Notification from './global/Notification';
import University from './global/University';
import Lesson from './global/Lesson';
import Tooltip from './global/Tooltip';
import Empty from '../templates/Empty';
import Backend from '../templates/Backend';
import Frontend from '../templates/Frontend';
import Wizard from '../templates/Wizard';
import Invoice from '../templates/Invoice';
import events from '../events/events';
import { methods } from '../helpers/channelChat';

import { mapState } from 'vuex';

export default
{
    name: 'Application',

    mixins: [methods],

    data()
    {
        return {
            updated: false,
            redirectTo: null,
            listeners: false, // Echo event listeners mounted?
        }
    },

    mounted()
    {
        let lang = this.$store.state.meta.lang;

        // just to show how to access the store directly
        console.info('The current language locale is: ' + lang);

        let locales;
        if (lang === 'de') {
            // Use the extended German locale file with country translations
            locales = require('../lang/locales.countries.de.js').default;
        } else if (lang === 'nl') {
            // Use the extended Dutch locale file with country translations
            locales = require('../lang/locales.countries.nl.js').default;
        } else {
            locales = require('../lang/locales.' + lang).default;
        }

        Vue.locale(lang, locales);

        if (lang != 'en') require('moment/locale/' + lang);

        events.$listen('app.updated', route =>
        {
            this.updated = true;
            if (route) this.redirectTo = route;
        });
    },

    created()
    {
        events.$listen('auth.loggedout', () =>
        {
            this.listeners = false;
        })

        events.$listen('app.authenticated', () =>
        {
            if (this.authenticated && ! this.listeners)
            {
                this.receiveUnreadChats()

                console.info('Websocket active.');

                Echo.channel('app')
                    .listen('AppUpdated', (e) =>
                    {
                        // only allow to update given environment (or all, if e.env is not set)
                        if (e.env && e.env != this.environment) return;

                        if (this.$store.state.app.loaded < e.updated)
                        {
                            this.updated = true;
                        }
                    });

                Echo.channel('account.' + this.$store.state.current.account.id)
                    .listen('Reinit', (e) =>
                    {
                        this.$store.dispatch('app/reinit', () =>
                        {
                            this.$store.dispatch('chat/setUnreadChats');
                        });
                    })
                    .listen('Broadcast.LocationUpdated', (e) =>
                    {
                        this.$store.dispatch('app/reinit', () =>
                        {
                            this.$store.dispatch('chat/setUnreadChats');
                        });
                    });

                Echo.channel('membership.' + this.$store.state.current.membership.id)
                    .listen('Logout', (e) =>
                    {
                        this.$store.dispatch('auth/logout', () =>
                        {
//                            Echo.channel('notification.' + this.$store.state.current.membership.id).leave()
                            this.$router.push(this.$onduro.route('login.signin'));
                        });
                    })
                    .listen('CheckoutBySupervisor', (e) =>
                    {
                        this.$store.dispatch('current/updateCheckin', false)
                    })
                    .listen('CheckinRefresh', (e) =>
                    {
                        events.$emit('reload-checkins');
                    })
                ;

                Echo.channel('notification.' + this.$store.state.current.membership.id)
                    .listen('ChatNotification', (e) =>
                    {
                        this.$store.dispatch('chat/setUnreadChats')
                        .then( unreadChats =>
                        {
                            if (this.activeChannel) this.refreshUnreadChats(unreadChats, this.activeChannel)
                            else this.receiveUnreadChats()
                        });
                    });

                this.listeners = true;
            }
        })
    },

    computed:
    {
        ...mapState({
            authenticated: ({ auth }) => auth.authenticated,
            hasNavigation: ({ layout }) => layout.hasNavigation,
            hasFooterBar: ({ layout }) => layout.hasFooterBar,
            viewport: ({ layout }) => layout.viewport,
            current: ({ current }) => current,
            ready: ({ app }) => app.ready,
            environment: ({ app }) => app.env,
            touchDevice: ({ app }) => app.touchDevice,
            maintenance: ({ app }) => app.maintenance,
            activeChannel: ({ chat }) => chat.activeChannel,
            unreadChats: ({ chat }) => chat.unreadChats
        }),

        backend()
        {
            return this.$route.meta.section == 'backend' && (this.authenticated);
        },

        frontend()
        {
            return this.$route.meta.section == 'frontend' || false;
        },

        empty()
        {
            return this.$route.meta.section == 'empty' || false;
        },

        wizard()
        {
            return this.$route.meta.section == 'wizard';
        },

        invoice()
        {
            return this.$route.meta.section == 'invoice' || false;
        },

        login()
        {
            return ! this.authenticated && ! this.empty && ! this.wizard
        },
    },

    methods:
    {
        updateApp()
        {
            if (this.redirectTo)
            {
                window.location.href = this.$onduro.route(this.redirectTo);
            } else {
                window.location.reload(true);
            }
        },

        receiveUnreadChats()
        {
            let ids = _.map(_.filter(this.unreadChats, chat => chat.receivedAt === null), item => item.statusId);

            if ( ! ids.length) return;

            this.$http.silent().put(
                '/api/memberships/unread-chats',
                {
                    ids: ids,
                    status: 'received'
                }).then(response =>
            {
                this.$store.commit('chat/SET_UNREAD_CHATS', response.data, {root: true})
            });
        }
    },

    components:
    {
        Modal,
        Flash,
        Widget,
        Loader,
        Invoice,
        Notification,
        Confirm,
        Empty,
        Backend,
        Frontend,
        Wizard,
        University,
        Lesson,
        Tooltip,
    }
}
</script>

<style lang="scss">
    @import '../../scss/onduro';
</style>



// WEBPACK FOOTER //
// App.vue?4a9bf3cf